import { useState, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import {
  PlusIcon,
  MinusIcon,
  ShoppingCartIcon,
  CpuChipIcon,
  ServerIcon,
  GiftIcon,
  CogIcon,
  CalculatorIcon
} from '@heroicons/react/24/outline'
import { motion } from 'framer-motion'

import { api } from '../services/api'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { Button } from '../components/Button'

// Types
interface PackageCustomization {
  base_package_id: string
  toolkit_addons: string[]
  custom_services: string[]
  physical_products: string[]
  custom_domain?: string
  additional_storage_gb: number
  additional_compute_units: number
}

interface PricingBreakdown {
  base_package_monthly: number
  toolkits_monthly: number
  custom_services_monthly: number
  physical_products_total: number
  shipping_total: number
  resource_upgrades_monthly: number
  total_monthly: number
  total_setup: number
  total_one_time: number
}

// Utility function to safely format prices that might be strings or numbers
const formatPrice = (price: any): string => {
  if (price === null || price === undefined) return '0.00'
  const numPrice = typeof price === 'string' ? parseFloat(price) : Number(price)
  return isNaN(numPrice) || !isFinite(numPrice) ? '0.00' : numPrice.toFixed(2)
}

export function PackageBuilderPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()

  // Debug logging
  console.log('PackageBuilderPage mounted with slug:', slug)

  const [customization, setCustomization] = useState<PackageCustomization>({
    base_package_id: '', // Will be set after fetching package details
    toolkit_addons: [],
    custom_services: [],
    physical_products: [],
    additional_storage_gb: 0,
    additional_compute_units: 0
  })

  const [selectedTab, setSelectedTab] = useState<'toolkits' | 'services' | 'swag' | 'resources'>('toolkits')
  const [packageType, setPackageType] = useState('media') // Will be determined from base package

  // Fetch base package info
  const { data: basePackage, isLoading: packageLoading, error: packageError } = useQuery({
    queryKey: ['package-by-slug', slug],
    queryFn: () => api.packages.getPackageBySlug(slug!),
    enabled: !!slug,
    retry: 2,
    onError: (error) => {
      console.error('Failed to fetch package by slug:', slug, error)
    }
  })

  // Fetch compatible toolkits
  const { data: toolkits = [], isLoading: toolkitsLoading } = useQuery({
    queryKey: ['compatible-toolkits', packageType],
    queryFn: () => api.packageBuilder.getCompatibleToolkits(packageType)
  })

  // Fetch custom services
  const { data: customServices = [], isLoading: servicesLoading } = useQuery({
    queryKey: ['custom-services'],
    queryFn: () => api.packageBuilder.getCustomServices()
  })

  // Fetch physical products
  const { data: physicalProducts = [], isLoading: productsLoading } = useQuery({
    queryKey: ['physical-products'],
    queryFn: () => api.packageBuilder.getPhysicalProducts()
  })

  // Calculate pricing
  const { data: pricing, refetch: recalculatePricing, error: pricingError } = useQuery({
    queryKey: ['package-pricing', customization],
    queryFn: () => api.packageBuilder.calculatePricing(customization),
    enabled: !!customization.base_package_id,
    retry: 1,
    onError: (error) => {
      console.error('Pricing calculation failed:', error)
    }
  })

  // Create quote mutation
  const createQuoteMutation = useMutation({
    mutationFn: (customization: PackageCustomization) =>
      api.packageBuilder.createQuote(customization),
    onSuccess: (quote) => {
      navigate(`/quotes/${quote.quote_id}`)
    }
  })

  // Update package type and ID based on base package
  useEffect(() => {
    if (basePackage?.package_type) {
      setPackageType(basePackage.package_type)
    }
    if (basePackage?.id) {
      setCustomization(prev => ({
        ...prev,
        base_package_id: basePackage.id
      }))
    }
  }, [basePackage])

  // Recalculate pricing when customization changes
  useEffect(() => {
    if (customization.base_package_id) {
      recalculatePricing()
    }
  }, [customization, recalculatePricing])

  const toggleToolkit = (toolkitId: string) => {
    setCustomization(prev => ({
      ...prev,
      toolkit_addons: prev.toolkit_addons.includes(toolkitId)
        ? prev.toolkit_addons.filter(id => id !== toolkitId)
        : [...prev.toolkit_addons, toolkitId]
    }))
  }

  const toggleService = (serviceId: string) => {
    setCustomization(prev => ({
      ...prev,
      custom_services: prev.custom_services.includes(serviceId)
        ? prev.custom_services.filter(id => id !== serviceId)
        : [...prev.custom_services, serviceId]
    }))
  }

  const toggleProduct = (productId: string) => {
    setCustomization(prev => ({
      ...prev,
      physical_products: prev.physical_products.includes(productId)
        ? prev.physical_products.filter(id => id !== productId)
        : [...prev.physical_products, productId]
    }))
  }

  const updateResources = (type: 'storage' | 'compute', value: number) => {
    const numValue = Number(value) || 0
    setCustomization(prev => ({
      ...prev,
      [type === 'storage' ? 'additional_storage_gb' : 'additional_compute_units']: Math.max(0, numValue)
    }))
  }

  if (packageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (packageError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Package</h1>
          <p className="text-gray-600 mb-4">
            Failed to load package "{slug}". Please try again.
          </p>
          <div className="space-y-2">
            <Button onClick={() => window.location.reload()} className="mr-2">
              Retry
            </Button>
            <Button onClick={() => navigate('/packages')} variant="outline">
              Back to Packages
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (!basePackage) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Package Not Found</h1>
          <p className="text-gray-600 mb-4">
            Package "{slug}" could not be found.
          </p>
          <Button onClick={() => navigate('/packages')}>
            Back to Packages
          </Button>
        </div>
      </div>
    )
  }

  const tabs = [
    { id: 'toolkits', name: 'MCP Toolkits', icon: CogIcon, count: toolkits.length },
    { id: 'services', name: 'Custom Services', icon: ServerIcon, count: customServices.length },
    { id: 'swag', name: 'Merchandise', icon: GiftIcon, count: physicalProducts.length },
    { id: 'resources', name: 'Resources', icon: CpuChipIcon, count: 0 }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Customize Your Package
              </h1>
              <p className="text-gray-600 mt-2">
                Building: <span className="font-medium">{basePackage.name}</span>
              </p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">Total Monthly</div>
              <div className="text-2xl font-bold text-primary-600">
                ${formatPrice(pricing?.total_monthly)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Base Package Info */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Base Package</h2>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-900">{basePackage.name}</h3>
                  <p className="text-gray-600 text-sm">{basePackage.short_description}</p>
                </div>
                <div className="text-right">
                  <div className="font-medium text-gray-900">${basePackage.monthly_price_display}</div>
                  <div className="text-sm text-gray-500">per month</div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="border-b border-gray-200">
                <nav className="flex">
                  {tabs.map((tab) => {
                    const Icon = tab.icon
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setSelectedTab(tab.id as any)}
                        className={`flex-1 py-4 px-6 text-center border-b-2 font-medium text-sm ${
                          selectedTab === tab.id
                            ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <Icon className="w-5 h-5" />
                          {tab.name}
                          {tab.count > 0 && (
                            <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                              {tab.count}
                            </span>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </nav>
              </div>

              <div className="p-6">
                {/* Toolkits Tab */}
                {selectedTab === 'toolkits' && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      Compatible MCP Toolkits
                    </h3>
                    {toolkitsLoading ? (
                      <LoadingSpinner />
                    ) : (
                      <div className="grid grid-cols-1 gap-4">
                        {toolkits.map((toolkit: any) => (
                          <div
                            key={toolkit.toolkit_id}
                            className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                              customization.toolkit_addons.includes(toolkit.toolkit_id)
                                ? 'border-primary-500 bg-primary-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => toggleToolkit(toolkit.toolkit_id)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <button
                                    className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                      customization.toolkit_addons.includes(toolkit.toolkit_id)
                                        ? 'border-primary-500 bg-primary-500'
                                        : 'border-gray-300'
                                    }`}
                                  >
                                    {customization.toolkit_addons.includes(toolkit.toolkit_id) && (
                                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                                    )}
                                  </button>
                                  <div>
                                    <h4 className="font-medium text-gray-900">{toolkit.name}</h4>
                                    <p className="text-sm text-gray-600">{toolkit.description}</p>
                                  </div>
                                </div>
                              </div>
                              <div className="text-right ml-4">
                                <div className="font-medium text-gray-900">
                                  ${formatPrice(toolkit.monthly_price)}
                                </div>
                                <div className="text-sm text-gray-500">per month</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Custom Services Tab */}
                {selectedTab === 'services' && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      Custom Services & Deployments
                    </h3>
                    {servicesLoading ? (
                      <LoadingSpinner />
                    ) : (
                      <div className="grid grid-cols-1 gap-4">
                        {customServices.map((service: any) => (
                          <div
                            key={service.service_id}
                            className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                              customization.custom_services.includes(service.service_id)
                                ? 'border-primary-500 bg-primary-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => toggleService(service.service_id)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <button
                                    className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                      customization.custom_services.includes(service.service_id)
                                        ? 'border-primary-500 bg-primary-500'
                                        : 'border-gray-300'
                                    }`}
                                  >
                                    {customization.custom_services.includes(service.service_id) && (
                                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                                    )}
                                  </button>
                                  <div>
                                    <h4 className="font-medium text-gray-900">{service.name}</h4>
                                    <p className="text-sm text-gray-600">{service.description}</p>
                                    <div className="text-xs text-gray-500 mt-1">
                                      Resources: {service.resources.cpu} CPU, {service.resources.memory_gb}GB RAM, {service.resources.storage_gb}GB Storage
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="text-right ml-4">
                                <div className="font-medium text-gray-900">
                                  ${formatPrice(service.monthly_price)}
                                </div>
                                <div className="text-sm text-gray-500">per month</div>
                                {service.setup_fee && (
                                  <div className="text-xs text-gray-500">
                                    +${service.setup_fee} setup
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Physical Products Tab */}
                {selectedTab === 'swag' && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      Merchandise & Physical Products
                    </h3>
                    {productsLoading ? (
                      <LoadingSpinner />
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {physicalProducts.map((product: any) => (
                          <div
                            key={product.product_id}
                            className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                              customization.physical_products.includes(product.product_id)
                                ? 'border-primary-500 bg-primary-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => toggleProduct(product.product_id)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <button
                                    className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                      customization.physical_products.includes(product.product_id)
                                        ? 'border-primary-500 bg-primary-500'
                                        : 'border-gray-300'
                                    }`}
                                  >
                                    {customization.physical_products.includes(product.product_id) && (
                                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                                    )}
                                  </button>
                                  <div>
                                    <h4 className="font-medium text-gray-900">{product.name}</h4>
                                    <p className="text-sm text-gray-600">{product.description}</p>
                                    <div className="text-xs text-gray-500 mt-1 capitalize">
                                      {product.category}
                                      {product.shipping_required && " • Shipping required"}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="text-right ml-4">
                                <div className="font-medium text-gray-900">
                                  ${formatPrice(product.price)}
                                </div>
                                <div className="text-sm text-gray-500">one-time</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Resources Tab */}
                {selectedTab === 'resources' && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-medium text-gray-900">
                      Additional Resources
                    </h3>

                    {/* Storage */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="font-medium text-gray-900">Additional Storage</h4>
                          <p className="text-sm text-gray-600">Add extra storage space beyond the base package</p>
                        </div>
                        <div className="text-right">
                          <div className="font-medium text-gray-900">
                            ${formatPrice((Number(customization.additional_storage_gb) || 0) * 0.1)}
                          </div>
                          <div className="text-sm text-gray-500">per month</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => updateResources('storage', (Number(customization.additional_storage_gb) || 0) - 10)}
                          className="p-2 rounded border border-gray-300 hover:bg-gray-50"
                          disabled={(Number(customization.additional_storage_gb) || 0) <= 0}
                        >
                          <MinusIcon className="w-4 h-4" />
                        </button>
                        <div className="flex-1 text-center">
                          <div className="text-lg font-medium">{customization.additional_storage_gb} GB</div>
                          <div className="text-sm text-gray-500">$0.10/GB per month</div>
                        </div>
                        <button
                          onClick={() => updateResources('storage', (Number(customization.additional_storage_gb) || 0) + 10)}
                          className="p-2 rounded border border-gray-300 hover:bg-gray-50"
                        >
                          <PlusIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Compute */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="font-medium text-gray-900">Additional Compute</h4>
                          <p className="text-sm text-gray-600">Add extra CPU and memory resources</p>
                        </div>
                        <div className="text-right">
                          <div className="font-medium text-gray-900">
                            ${formatPrice((Number(customization.additional_compute_units) || 0) * 15)}
                          </div>
                          <div className="text-sm text-gray-500">per month</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => updateResources('compute', (Number(customization.additional_compute_units) || 0) - 1)}
                          className="p-2 rounded border border-gray-300 hover:bg-gray-50"
                          disabled={(Number(customization.additional_compute_units) || 0) <= 0}
                        >
                          <MinusIcon className="w-4 h-4" />
                        </button>
                        <div className="flex-1 text-center">
                          <div className="text-lg font-medium">{customization.additional_compute_units} Units</div>
                          <div className="text-sm text-gray-500">$15/unit per month (2 CPU, 4GB RAM each)</div>
                        </div>
                        <button
                          onClick={() => updateResources('compute', (Number(customization.additional_compute_units) || 0) + 1)}
                          className="p-2 rounded border border-gray-300 hover:bg-gray-50"
                        >
                          <PlusIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Pricing Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-8">
              <div className="flex items-center gap-2 mb-6">
                <CalculatorIcon className="w-5 h-5 text-primary-600" />
                <h3 className="text-lg font-semibold text-gray-900">Pricing Summary</h3>
              </div>

              {pricing ? (
                <div className="space-y-4">
                  {/* Monthly Costs */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Base Package</span>
                      <span className="font-medium">${formatPrice(pricing.base_package_monthly)}</span>
                    </div>
                    {parseFloat(pricing.toolkits_monthly || '0') > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Toolkits</span>
                        <span className="font-medium">${formatPrice(pricing.toolkits_monthly)}</span>
                      </div>
                    )}
                    {parseFloat(pricing.custom_services_monthly || '0') > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Custom Services</span>
                        <span className="font-medium">${formatPrice(pricing.custom_services_monthly)}</span>
                      </div>
                    )}
                    {parseFloat(pricing.resource_upgrades_monthly || '0') > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Resource Upgrades</span>
                        <span className="font-medium">${formatPrice(pricing.resource_upgrades_monthly)}</span>
                      </div>
                    )}
                  </div>

                  <div className="border-t border-gray-200 pt-2">
                    <div className="flex justify-between font-semibold">
                      <span>Monthly Total</span>
                      <span className="text-primary-600">${formatPrice(pricing.total_monthly)}</span>
                    </div>
                  </div>

                  {/* One-time Costs */}
                  {(parseFloat(pricing.total_setup || '0') > 0 || parseFloat(pricing.total_one_time || '0') > 0) && (
                    <div className="border-t border-gray-200 pt-4 space-y-2">
                      {parseFloat(pricing.total_setup || '0') > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Setup Fees</span>
                          <span className="font-medium">${formatPrice(pricing.total_setup)}</span>
                        </div>
                      )}
                      {parseFloat(pricing.physical_products_total || '0') > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Merchandise</span>
                          <span className="font-medium">${formatPrice(pricing.physical_products_total)}</span>
                        </div>
                      )}
                      {parseFloat(pricing.shipping_total || '0') > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Shipping</span>
                          <span className="font-medium">${formatPrice(pricing.shipping_total)}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="border-t border-gray-200 pt-6 space-y-3">
                    <Button
                      onClick={() => createQuoteMutation.mutate(customization)}
                      disabled={createQuoteMutation.isPending}
                      className="w-full"
                      size="lg"
                    >
                      {createQuoteMutation.isPending ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <>
                          <ShoppingCartIcon className="w-5 h-5 mr-2" />
                          Get Quote
                        </>
                      )}
                    </Button>

                    <Button
                      variant="outline"
                      onClick={() => navigate('/packages')}
                      className="w-full"
                    >
                      Back to Packages
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}